import { useEffect, useState } from "react";

import {
  alpha,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Link,
  OutlinedInput,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { updatePublicNickname } from "API/calls";
import { Check, InformationCircle, TimeClockCircle } from "components/icons";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useUserDataContext } from "context/UserDataContext";
import useConfirm from "hooks/useConfirm";
import useKeyPress from "hooks/useKeyPress";
import { useSnackbar } from "notistack";
import { Trans, useTranslation } from "react-i18next";
import { PublicNicknameState, SubscriptionType } from "types/enums";

const HelperBox = styled(Box, { shouldForwardProp: (prop) => prop !== "color" })<{ color: string }>(
  ({ color, theme }) => {
    return {
      backgroundColor: alpha(color, 0.1),
      color: color,
      display: "flex",
      alignItems: "center",
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0.75),
      borderRadius: "8px",
      gap: theme.spacing(0.5),
      width: "fit-content",
    };
  }
);

const OutlinedInputStyled = styled(OutlinedInput)(({ disabled }) => ({
  paddingRight: "7px",
  borderRadius: "16px",
  backgroundColor: disabled ? "#2A2E3E" : undefined,

  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: "16px",
  },

  "& .MuiOutlinedInput-input.Mui-disabled": {
    WebkitTextFillColor: "#959595",
  },

  "& .MuiInputAdornment-root .MuiButton-root:disabled": {
    backgroundColor: alpha("#797575", 0.2),
  },
}));

const REGISTERED_NICKNAME_STATES = [PublicNicknameState.PENDING, PublicNicknameState.ACTIVE];

const Nickname = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { colors } = useMUIThemeModeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { subscriptionType, publicNickname, refreshData } = useUserDataContext();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm } = useConfirm();

  const isPRO = subscriptionType === SubscriptionType.PRO;
  const [nickname, setNickname] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  const keyPressEnter = useKeyPress("Enter");

  useEffect(() => {
    if (publicNickname?.publicNickname) {
      setNickname(publicNickname.publicNickname);
    }
  }, [publicNickname?.publicNickname]);

  useEffect(() => {
    if (nickname.length && keyPressEnter && isInputFocused) {
      handleRegisterNickname();
    }
  }, [keyPressEnter]);

  const handleRegisterNickname = async () => {
    if (!nickname) return;

    setIsLoading(true);
    try {
      await updatePublicNickname(nickname.trim());
      refreshData?.();
      enqueueSnackbar(t("dashboard.communityHub.atlas.nickname.success"), { variant: "success" });
    } catch (error: any) {
      if (error?.response?.status === 409) {
        enqueueSnackbar(t("dashboard.communityHub.atlas.nickname.error409"), { variant: "warning" });
      } else {
        enqueueSnackbar(t("dashboard.communityHub.atlas.nickname.error"), { variant: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnregisterNickname = async () => {
    try {
      if (publicNickname?.publicNicknameState === PublicNicknameState.ACTIVE && isPRO) {
        await confirm({
          title: t("dashboard.communityHub.atlas.nickname.cancelConfirm.title"),
          description: t("dashboard.communityHub.atlas.nickname.cancelConfirm.description"),
          dialogProps: { maxWidth: "xs" },
          cancellationText: t("dashboard.communityHub.atlas.nickname.cancelConfirm.cancel"),
          confirmationText: t("dashboard.communityHub.atlas.nickname.cancelConfirm.confirm"),
        });
      }

      setIsLoading(true);
      try {
        await updatePublicNickname("");
        setNickname("");
        refreshData?.();
        enqueueSnackbar(t("dashboard.communityHub.atlas.nickname.updateSuccess"), { variant: "success" });
      } catch (error: any) {
        enqueueSnackbar(t("dashboard.communityHub.atlas.nickname.updateError"), { variant: "error" });
      } finally {
        setIsLoading(false);
      }
    } catch (err: any) {
      // silent
    }
  };

  const getHelperConfig = () => {
    const iconSize = { width: isMobile ? "14px" : "17px", height: isMobile ? "14px" : "17px" };

    if (!isPRO) {
      return {
        icon: <InformationCircle style={iconSize} />,
        key: "disabled",
        color: theme.palette.tertiary.main,
        href: "/atlas#podporovatele",
      };
    }

    switch (publicNickname?.publicNicknameState) {
      case PublicNicknameState.PENDING:
        return {
          icon: <TimeClockCircle style={iconSize} />,
          key: "pending",
          color: theme.palette.primary.main,
          href: "",
        };
      case PublicNicknameState.ACTIVE:
        return {
          icon: <Check style={iconSize} />,
          key: "success",
          color: colors.success,
          href: "/atlas#podporovatele",
        };
      default:
        return {
          icon: <InformationCircle style={iconSize} />,
          key: "empty",
          color: theme.palette.tertiary.main,
          href: "/atlas#podporovatele",
        };
    }
  };

  const helperText = () => {
    const config = getHelperConfig();

    return (
      <HelperBox color={config.color}>
        {config.icon}
        <Typography color={"inherit"} fontSize={{ xs: "0.625rem", md: "0.8125rem" }}>
          <Trans
            i18nKey={`dashboard.communityHub.atlas.nickname.helperText.${config.key}`}
            components={{ a: <Link href={config.href} color="inherit" /> }}
          />
        </Typography>
      </HelperBox>
    );
  };

  const actionButton = () => {
    let onClick = handleRegisterNickname;
    let label = t("dashboard.communityHub.atlas.nickname.send");
    let color = "primary";

    const isRegistered = publicNickname?.publicNicknameState && REGISTERED_NICKNAME_STATES.includes(publicNickname.publicNicknameState);
    const isDisabled = isLoading || (isRegistered ? false : !isPRO) || !nickname.length;

    if (isRegistered) {
      label = t(`dashboard.communityHub.atlas.nickname.${isPRO ? "edit" : "cancel"}`);
      onClick = handleUnregisterNickname;
      color = "error";
    }

    return (
      <Button
        variant="contained"
        color={color as "primary" | "error"}
        size={isMobile ? "small" : "large"}
        disabled={isDisabled}
        sx={{ backgroundColor: color === "error" ? colors.error : undefined }}
        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
        onClick={onClick}>
        {label}
      </Button>
    );
  };

  return (
    <Stack spacing={1}>
      <OutlinedInputStyled
        notched={false}
        fullWidth
        placeholder={t("dashboard.communityHub.atlas.nickname.label")}
        value={nickname}
        size={isMobile ? "small" : "medium"}
        onChange={(e) => setNickname(e.target.value)}
        onFocus={() => setIsInputFocused(true)}
        onBlur={() => setIsInputFocused(false)}
        disabled={isLoading || !isPRO}
        readOnly={publicNickname?.publicNicknameState !== PublicNicknameState.INACTIVE}
        inputProps={{ maxLength: 30 }}
        endAdornment={<InputAdornment position="end">{actionButton()}</InputAdornment>}
      />
      {helperText()}
    </Stack>
  );
};

export default Nickname;
